/**
 * Auth API Service
 * 
 * Provides standardized methods for authentication and user management
 */
import api from './api';

/**
 * Auth service with standardized methods for authentication operations
 */
export const authService = {
  /**
   * Login with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Authentication result with token and user data
   */
  async login(email, password) {
    // Using direct fetch here because we don't have a token yet for the api client
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });

      // Process the response
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.message || 'Login failed';
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      const responseData = await response.json();
      
      // Handle standardized response format
      if (responseData.success === true && responseData.data !== undefined) {
        return responseData.data;
      }
      
      // Fall back to legacy format
      return responseData;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Verify the current authentication token
   * @returns {Promise<Object>} User data if authenticated
   */
  async verifyAuth() {
    return api.get('auth/verify');
  },

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile data
   */
  async getUserProfile() {
    return api.get('auth/user');
  },

  /**
   * Change user password
   * @param {Object} passwordData - Password change data
   * @returns {Promise<Object>} Password change result
   */
  async changePassword(passwordData) {
    return api.post('auth/change-password', passwordData);
  },

  /**
   * Request password reset
   * @param {string} email - User email
   * @returns {Promise<Object>} Reset request result
   */
  async requestPasswordReset(email) {
    return api.post('auth/forgot-password', { email });
  },

  /**
   * Reset password with token
   * @param {Object} resetData - Reset data with token and new password
   * @returns {Promise<Object>} Reset result
   */
  async resetPassword(resetData) {
    return api.post('auth/reset-password', resetData);
  },

  /**
   * Validate a token format (Sanctum plain text token or JWT)
   * @param {string} tokenValue - Token to validate
   * @returns {boolean} Whether the token has valid format
   */
  isTokenValid(tokenValue) {
    if (!tokenValue) {return false;}
    
    // For Laravel Sanctum tokens (plain text format like "1|randomstring")
    if (tokenValue.includes('|')) {
      const parts = tokenValue.split('|');
      return parts.length === 2 && parts[0].match(/^\d+$/) && parts[1].length > 0;
    }
    
    // For JWT tokens (legacy support)
    try {
      // Check token structure (must have 3 parts separated by dots)
      const parts = tokenValue.split('.');
      if (parts.length !== 3) {
        return false;
      }
      
      // Decode payload with proper base64url handling
      const payloadBase64 = parts[1];
      // Handle base64url format by replacing characters and padding
      let base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
      // Add padding if needed
      switch (base64.length % 4) {
        case 0: break; // No padding needed
        case 2: base64 += '=='; break;
        case 3: base64 += '='; break;
      }
      
      const payload = JSON.parse(atob(base64));
      
      // Check for EITHER user_id OR id - both formats exist in the system
      if ((!payload.user_id && !payload.id) || !payload.email) {
        return false;
      }
      
      // Check expiry if present
      if (payload.exp && payload.exp < Math.floor(Date.now() / 1000)) {
        return false;
      }
      
      return true;
    } catch (error) {
      return false;
    }
  },
};

export default authService;